// @flow
import React from 'react'

import Button from '../controls/PrimaryButton'
import styled from 'styled-components'
import Modal from '../FieldSet/Modal'
import ErrorText from '../controls/ErrorText'
import AutoSizeTextArea from 'react-textarea-autosize'
import MediaLoaderContainer from '../AdEdit/MediaLoaderContainer'
import type { Configs, Crop, ImageSizeRestrictions, Media } from '../types'
import type { SocialPublicProps } from '../../redux/ducks/audienceEdit/reducers'
import SocialPublicSelect from '../AudienceEdit/SocialPublicSelect'
import PreviewImageCropper from '../AdEdit/PreviewImageCropper'
import QuestionsContainer from './QuestionsContainer'

const FieldWrapper = styled.div`
  font-size: 14px;
  padding-bottom: 12px;
`

const fieldCommonStyles = `
  width: 100%;
  height: 35px;
  border: 1px solid #ccc;
  border-radius: 5px;
  padding: 0 15px;
  margin: 0 5px 0 0;
  text-align: left;
`

const TextInput = styled.input`
  ${fieldCommonStyles};
  ${({ hasErrors }: Object) => hasErrors && 'border-color: red;'};
`

const TextArea: typeof AutoSizeTextArea = styled(AutoSizeTextArea)`
  ${fieldCommonStyles};
  padding-top: 10px;
  padding-bottom: 10px;
  resize: vertical;
  ${({ hasErrors }: Object) => hasErrors && 'border-color: red;'};
`

const Label = styled.div`
  padding: 3px 0;
  font-size: 14px;
`

const t = {
  header: 'Лид-форма',
  create: 'Создать',
  update: 'Обновить',
  name: 'Название',
  title: 'Заголовок',
  description: 'Описание',
  image: 'Картинка',
  vk_crop: 'Обрезка для ВКонтакте',
  questions: 'Вопросы',
  privacy_policy: 'Ссылка на политику конфиденциальности',
  confirmation: 'Подтверждение',
  vk_group: 'Группа ВКонтакте',
  select_vk_group: 'Выберите группу..',
  fb_group: 'Страница Facebook',
  select_fb_group: 'Выберите страницу..',
  name_placeholder: 'Будет отображаться в списке лид-форм',
  title_placeholder: 'Заголовок лид-формы',
  description_placeholder: 'Описание лид-формы',
  privacy_placeholder: 'Ссылка на документ или страницу',
  confirmation_placeholder: 'Текст, отображаемый после заполнения лид-формы'
}

type Errors = Array<string>

export type Props = {|
  id: number | null,
  configs: Configs,
  isOrtAccounts: boolean,
  adAccountId: number,
  name: string,
  vkGroup: {| id: number, label: string |} | null,
  fbGroup: {| id: number, label: string |} | null,
  title: string,
  description: string,
  imageStoreId: string,
  vkCrop: Crop | null,
  image: ?Media,
  privacyPolicyUrl: string,
  confirmationText: string,
  nameErrors: Errors,
  vkGroupErrors: Errors,
  fbGroupErrors: Errors,
  titleErrors: Errors,
  descriptionErrors: Errors,
  imageErrors: Errors,
  questionsErrors: Errors,
  privacyPolicyUrlErrors: Errors,
  confirmationTextErrors: Errors,
  socialPublicProps: SocialPublicProps,
  isOpen: boolean,
  isLoading: boolean,
  onChange: Function,
  onClose: Function,
  onPersist: Function
|}

export default function Form({ onChange, isLoading, ...props }: Props) {
  const imageConfig = props.configs.vkLeadformConfigs
  const imageSizeRestrictions: ImageSizeRestrictions = {
    minWidth: imageConfig.minWidth,
    minHeight: imageConfig.minHeight,
    maxHeight: imageConfig.maxHeight,
    maxWidth: imageConfig.maxWidth
  }
  return (
    <Modal
      maxWidth={500}
      header={t.header}
      isOpen={props.isOpen}
      hideBtn
      onClose={() => props.onClose()}
    >
      <FieldWrapper>
        <Label>{t.name}</Label>
        <TextInput
          className="t-Name"
          placeholder={t.name_placeholder}
          disabled={isLoading}
          value={props.name}
          onChange={(e) => onChange({ name: e.target.value, nameErrors: [] })}
        />
        <ErrorText errors={props.nameErrors} />
      </FieldWrapper>

      <FieldWrapper className="t-VkGroup">
        <Label>{t.vk_group}</Label>
        <SocialPublicSelect
          isOrtAccounts={props.isOrtAccounts}
          currentAdAccountId={props.adAccountId}
          publicObject={
            (props.vkGroup && {
              id: props.vkGroup.id,
              label: props.vkGroup.label,
              image_url: ''
            }) ||
            void 0
          }
          onPublicChange={(publicObject) => onChange({ vkGroup: publicObject, vkGroupErrors: [] })}
          placeholder={t.select_vk_group}
          isEditDisabled={!!props.id && !!props.vkGroup}
          publicProps={props.socialPublicProps.vkProps}
          commonPublicProps={props.socialPublicProps.common}
        />
        <ErrorText errors={props.vkGroupErrors} />
      </FieldWrapper>

      <FieldWrapper className="t-FbGroup">
        <Label>{t.fb_group}</Label>
        <SocialPublicSelect
          isOrtAccounts={props.isOrtAccounts}
          currentAdAccountId={props.adAccountId}
          publicObject={
            (props.fbGroup && {
              id: props.fbGroup.id,
              label: props.fbGroup.label,
              image_url: ''
            }) ||
            void 0
          }
          onPublicChange={(publicObject) => onChange({ fbGroup: publicObject, fbGroupErrors: [] })}
          placeholder={t.select_fb_group}
          isEditDisabled={!!props.id && !!props.fbGroup}
          publicProps={props.socialPublicProps.fbProps}
          commonPublicProps={props.socialPublicProps.common}
        />
        <ErrorText errors={props.fbGroupErrors} />
      </FieldWrapper>

      <FieldWrapper>
        <Label>{t.title}</Label>
        <TextInput
          className="t-Title"
          placeholder={t.title_placeholder}
          disabled={isLoading}
          value={props.title}
          onChange={(e) => onChange({ title: e.target.value, titleErrors: [] })}
        />
        <ErrorText errors={props.titleErrors} />
      </FieldWrapper>

      <FieldWrapper>
        <Label>{t.description}</Label>
        <TextInput
          className="t-Description"
          placeholder={t.description_placeholder}
          disabled={isLoading}
          value={props.description}
          onChange={(e) => onChange({ description: e.target.value, descriptionErrors: [] })}
        />
        <ErrorText errors={props.descriptionErrors} />
      </FieldWrapper>

      <FieldWrapper className="t-Image">
        <Label>{t.image}</Label>
        <MediaLoaderContainer
          mediaType="image"
          storeId={props.imageStoreId}
          onLoad={(storeId) => onChange({ imageStoreId: storeId, imageErrors: [] })}
          imageSizeRestrictions={imageSizeRestrictions}
        />
        {props.image && (
          <PreviewImageCropper
            title={<Label>{t.vk_crop}</Label>}
            image={props.image}
            crop={props.vkCrop}
            config={imageConfig}
            onChange={(crop) => onChange({ vkCrop: crop })}
          />
        )}
        <ErrorText errors={props.imageErrors} />
      </FieldWrapper>

      <FieldWrapper>
        <Label>{t.privacy_policy}</Label>
        <TextInput
          className="t-PrivacyPolicy"
          placeholder={t.privacy_placeholder}
          disabled={isLoading}
          value={props.privacyPolicyUrl}
          onChange={(e) =>
            onChange({ privacyPolicyUrl: e.target.value, privacyPolicyUrlErrors: [] })
          }
        />
        <ErrorText errors={props.privacyPolicyUrlErrors} />
      </FieldWrapper>

      <FieldWrapper>
        <Label>{t.questions}</Label>
        <QuestionsContainer />
        <ErrorText errors={props.questionsErrors} />
      </FieldWrapper>

      <FieldWrapper>
        <Label>{t.confirmation}</Label>
        <TextArea
          className="t-ConfirmationText"
          placeholder={t.confirmation_placeholder}
          disabled={isLoading}
          value={props.confirmationText}
          onChange={(e) =>
            onChange({ confirmationText: e.target.value, confirmationTextErrors: [] })
          }
        />
        <ErrorText errors={props.confirmationTextErrors} />
      </FieldWrapper>

      <Button className="t-Create" onClick={() => props.onPersist()} disabled={isLoading}>
        {props.id === null ? t.create : t.update}
      </Button>
    </Modal>
  )
}
